// UseGameState.js
import { useEffect, useCallback } from 'react';
import { useGameState } from './GameStateContext';
import { Chess } from 'chess.js';
import { getPositionData } from '../../util/PositionData';
import { 
  getNodeAtPathInfinite,
  getPathDepth,
  parseVariationPath,
  buildVariationPath,
  isInVariation,
  getParentPath,
  getNextMovePath,
  getPreviousMovePath,
  validatePath,
  getMoveSequenceToPath
} from '../../util/VariationPathUtils';

/**
 * Get stored FEN directly from moves array for any move (main line or nested variation)
 * @param {Array} moves - The full move list
 * @param {string} moveId - The move ID (e.g., '4' or '4|0:2' or '4|0:1|1:0')
 * @returns {string|null} - The stored FEN or null if not found
 */
function getStoredFENFromMoves(moves, moveId, gameState = null) {
  if (!moves || !moveId || moveId === '-1') {
    return null;
  }

  // Handle starting position variations (e.g., "-1|0:0")
  if (moveId.startsWith('-1|')) {
    if (!gameState || !gameState.startPosition || !gameState.startPosition.variations) {
      return null;
    }
    
    try {
      const parts = moveId.split('|');
      if (parts.length >= 2) {
        const variationPart = parts[1];
        const [varIdx, moveIdx] = variationPart.split(':').map(Number);
        
        const variation = gameState.startPosition.variations[varIdx];
        const move = variation?.[moveIdx];
        
        if (move && move.fen) {
          // console.log('✅ Found stored FEN for starting position variation:', moveId, move.fen);
          return move.fen;
        }
      }
    } catch (err) {
      console.error('Error getting stored FEN for starting position variation:', err);
    }
    
    return null;
  }

  // Handle regular variation moves with infinite nesting support
  if (moveId.includes('|')) {
    // Use the path utilities to navigate to the target move
    const result = getNodeAtPathInfinite(moves, moveId);
    
    
    
    return result.success ? (result.node?.fen || null) : null;
  } else {
    // Handle main line moves
    const moveIndex = parseInt(moveId, 10);
    if (moveIndex >= 0 && moveIndex < moves.length) {
      const moveData = moves[moveIndex];
      
      return moveData?.fen || null;
    }
  }
  
  return null;
}

/**
 * Calculate the current board position based on a move path (with nested variation support)
 * @param {Array} moves - The full move list
 * @param {string} currentMove - The current move path (e.g., '0|0:1' or '4|0:1|1:2|0:0')
 * @param {string} initialFEN - The initial position FEN
 * @returns {string} - FEN representation of the position
 */
function getCurrentBoardPosition(moves, currentMove, initialFEN = null, gameData = null) {


  if (currentMove === '-1') {
    const startFEN = initialFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    
    return startFEN;
  }
  
  // Handle starting position variations early to prevent fallback calculations
  if (currentMove && currentMove.startsWith('-1|')) {
    try {
      const parts = currentMove.split('|');
      if (parts.length >= 2) {
        const variationPart = parts[1];
        const [varIdx, moveIdx] = variationPart.split(':').map(Number);
        
        // Access starting position variations from gameData if available
        if (gameData && gameData.startPosition && gameData.startPosition.variations) {
          const variation = gameData.startPosition.variations[varIdx];
          if (variation && Array.isArray(variation)) {
            const chess = new Chess(initialFEN);
            
            // Apply moves from the starting position variation up to the current move
            for (let i = 0; i <= moveIdx && i < variation.length; i++) {
              try {
                chess.move(variation[i].notation);
              } catch (err) {
                console.error(`Error applying starting position variation move ${variation[i].notation}:`, err);
                break;
              }
            }
            
            return chess.fen();
          }
        }
      }
    } catch (err) {
      console.error('Error processing starting position variation in getCurrentBoardPosition:', err);
    }
    
    // Fallback to start position for malformed starting position variations
    return initialFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  }

  try {
    const chess = new Chess(initialFEN);
    
    
    // Handle variation moves with nested support
    if (currentMove.includes('|')) {
      
      
      // Use the path utilities to get the move sequence
      const moveSequence = getMoveSequenceToPath(moves, currentMove, initialFEN);
      
      
      
      // Play all moves in the sequence
      for (let i = 0; i < moveSequence.length; i++) {
        const notation = moveSequence[i];
        
        
        
        const result = chess.move(notation);
          if (!result) {
          console.error(`🔍 BOARDBUG [getCurrentBoardPosition] FAILED to play move ${i}:`, {
            notation,
              currentFEN: chess.fen()
            });
            break;
          } else {
          
        }
      }
      
      const variationFinalFEN = chess.fen();
      
      
      return variationFinalFEN;
    }
    
    // Handle main line moves (simple integer indices)
    const moveIndex = parseInt(currentMove, 10);
    
    

    if (isNaN(moveIndex) || moveIndex < 0 || moveIndex >= moves.length) {
      console.warn('🔍 BOARDBUG [getCurrentBoardPosition] Invalid move index:', {
        currentMove,
        moveIndex,
        movesLength: moves.length
      });
      return initialFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
    }

    // Play moves up to and including the current move
    for (let i = 0; i <= moveIndex; i++) {
      const move = moves[i];
      if (move && move.notation) {
        

        const result = chess.move(move.notation);
        if (!result) {
          console.error(`🔍 BOARDBUG [getCurrentBoardPosition] FAILED main line move ${i}:`, {
            notation: move.notation,
            currentFEN: chess.fen()
          });
          break;
        }
      }
    }
    
    const mainLineFinalFEN = chess.fen();
    
    return mainLineFinalFEN;
  } catch (error) {
    console.error('🔍 BOARDBUG [getCurrentBoardPosition] ERROR:', {
      error: error.message,
      currentMove,
      stack: error.stack
    });
    return initialFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
  }
}

const useGameLogic = () => {

  const { state, dispatch } = useGameState();
const { moves, currentMove, initialFEN, orientation, starredMoves } = state;


  const goToMove = (id) => {
    // Validate the move ID before proceeding
    if (id === null || id === undefined) {
      console.warn('[goToMove] Invalid move ID (null/undefined), ignoring');
      return;
    }
    
    // Convert to string and validate
    const moveId = String(id);
    
    // Check for invalid string values
    if (moveId === '' || moveId === 'null' || moveId === 'undefined') {
      console.warn('[goToMove] Invalid move ID string:', moveId, 'ignoring');
      return;
    }

    
    
    // Try to get stored FEN directly from moves array first
    let fen = getStoredFENFromMoves(moves, moveId);
    
    if (fen) {
      
    } else {
      // Fallback to calculation if no stored FEN found
      
      fen = getCurrentBoardPosition(moves, moveId, state.initialFEN);
      
    }
    
    // First update the current move id
    
    dispatch({ type: 'GOTO_MOVE', payload: moveId });
    
    // Then immediately update the FEN to ensure the board gets updated
    // Only do this for navigation, not for making new moves
    const shouldUpdateFEN = moveId !== String(moves.length);
    
    
    if (shouldUpdateFEN) {
      
      dispatch({ type: 'SET_FEN', payload: fen });
    } else {
      
    }

    
  };
  const setMoves = (moves) => dispatch({ type: 'SET_MOVES', payload: moves });
  const setInitialPosition = (fen) => dispatch({ type: 'SET_INITIAL_POSITION', payload: fen });
  
  /**
   * Get the next expected move at the current position
   * @returns {Object|null} - The next move object if it exists, null otherwise
   */
  const getNextMove = () => {
    if (currentMove === '-1') {
      // At start position, next move is index 0 (unless there are starting position variations)
      if (state.startPosition?.variations?.length > 0) {
        // There are starting position variations, but we still prioritize main line
        return moves.length > 0 ? { move: moves[0], id: '0' } : null;
      }
      return moves.length > 0 ? { move: moves[0], id: '0' } : null;
    }
    
    if (currentMove.includes('|')) {
      // We're in a variation
      const parts = currentMove.split('|');
      const lastPart = parts[parts.length - 1];
      const [varIdx, moveIdx] = lastPart.split(':').map(Number);
      
      // Find the current variation
      const parentPath = parts.slice(0, -1).join('|');
      let currentVariation = null;
      
      // ENHANCED: Handle start position variations
      if (parentPath === '-1') {
        // This is a start position variation
        currentVariation = state?.startPosition?.variations?.[varIdx];
      } else if (parentPath.includes('|')) {
        // Nested variation
        const parentInfo = getNodeAtPath(parentPath);
        currentVariation = parentInfo.node?.variations?.[varIdx];
      } else {
        // Top-level variation
        const parentIdx = parseInt(parentPath, 10);
        currentVariation = moves[parentIdx]?.variations?.[varIdx];
      }
      
      if (currentVariation && moveIdx + 1 < currentVariation.length) {
        const nextMoveId = parts.slice(0, -1).join('|') + `|${varIdx}:${moveIdx + 1}`;
        return { move: currentVariation[moveIdx + 1], id: nextMoveId };
      }
      
      return null;
    } else {
      // We're in the main line
      const idx = parseInt(currentMove, 10);
      if (idx + 1 < moves.length) {
        return { move: moves[idx + 1], id: String(idx + 1) };
      }
      return null;
    }
  };

  /**
   * Record a new move at the current position with duplicate prevention
   * @param {string} notation - The move in SAN notation (e.g., "e4", "Nf3")
   * @param {string} fromSquare - The source square of the move (e.g., "e2")
   * @param {string} toSquare - The target square of the move (e.g., "e4")
   * @param {Object} additionalData - Optional additional data for the move (arrows, highlightedSquares, etc.)
   */
  const recordMove = (notation, fromSquare, toSquare, additionalData = {}) => {
    
    
    // PUZZLE MODE: Prevent NEW variations in puzzle mode (but allow existing moves)
    // Check if this is a puzzle by looking at tags in additionalData or moves
    const isPuzzle = additionalData.isPuzzle || 
                     (typeof window !== 'undefined' && window.isPuzzleMode) ||
                     false;
    
    if (isPuzzle) {
      // Check if we would be creating a variation (not at end of line)
      const notAtEndOfLine = (currentMove !== '-1' && 
                             !currentMove.includes('|') && 
                             parseInt(currentMove, 10) < moves.length - 1) ||
                            (currentMove === '-1' && moves.length > 0);
      
      if (notAtEndOfLine) {
        // First, check if the next move already exists and matches this move
        const nextMove = getNextMove();
        const isPlayingExistingMove = nextMove && nextMove.move.notation === notation;
        
        if (!isPlayingExistingMove) {
          // console.log('🔒 [UseGameState] Puzzle mode: Preventing NEW variation move -', notation, 'Expected:', nextMove?.move.notation);
          return; // Exit early - don't create NEW variations in puzzle mode
        } else {
          // console.log('✅ [UseGameState] Puzzle mode: Allowing existing move -', notation);
          // Continue with normal flow to navigate to the existing move
        }
      }
    }
    
    // First, check if the next move already exists and matches
    const nextMove = getNextMove();
    if (nextMove && nextMove.move.notation === notation) {
      
      goToMove(nextMove.id);
      return;
    }
    
    // Handle starting position variations
    if (currentMove === '-1' && moves.length > 0) {
      // We're at the starting position but there are already moves - create a variation
      const newVariationMove = { 
        notation, 
        from: fromSquare, 
        to: toSquare,
        ...additionalData
      };
      const newVariation = [newVariationMove];
      
      // NEW: In ViewGame mode, find the next available variation index to avoid conflicts
      let variationIndex = 0;
      if (additionalData.isViewMode && state.startPosition?.variations) {
        // Find the next available variation index
        variationIndex = state.startPosition.variations.length;
        // console.log('[recordMove] ViewGame mode: using variation index', variationIndex, 'to avoid conflicts');
      }
      
      dispatch({ 
        type: 'ADD_VARIATION_AT_PATH', 
        payload: { 
          path: '-1',
          variation: newVariation
        }
      });
      
      // Navigate to the new starting position variation using the appropriate index
      const newMoveId = `-1|${variationIndex}:0`;
      // console.log('[recordMove] Navigating to starting position variation:', newMoveId);
      dispatch({ type: 'GOTO_MOVE', payload: newMoveId });
      return;
    }
    
    // If we're not at the end of the current line, we need to create a variation
    if (currentMove !== '-1' && 
        !currentMove.includes('|') && 
        parseInt(currentMove, 10) < moves.length - 1) {
      
      
      
      // We're in the middle of the main line, create a variation
      const parentIdx = parseInt(currentMove, 10);
      const parentMove = moves[parentIdx];
      
      // Create or append to variations
      const variations = parentMove.variations || [];
      
      // The actual move structure creation is handled in GameStateContext
      // Here we just provide the notation and square information
      const newVariationMove = { 
        notation, 
        from: fromSquare, 
        to: toSquare,
        ...additionalData // Include any additional data like arrows or highlightedSquares
      };
      const newVariation = [newVariationMove];
      
      dispatch({ 
        type: 'ADD_VARIATION', 
        payload: { 
          parentIdx,
          variation: newVariation
        }
      });
      
      // Navigate to the new variation
      const newMoveId = `${parentIdx}|${variations.length}:0`;
      
      
      // Update current move ID without recalculating FEN
      dispatch({ type: 'GOTO_MOVE', payload: newMoveId });
    } 
    else if (currentMove.includes('|')) {
      // Handle starting position variations differently
      if (currentMove.startsWith('-1|')) {
        // We're in a starting position variation
        const parts = currentMove.split('|');
        const lastPart = parts[parts.length - 1];
        const [varIdx, moveIdx] = lastPart.split(':').map(Number);
        
        // Get current starting position and its variations
        const startPos = state.startPosition;
        if (!startPos || !startPos.variations || !Array.isArray(startPos.variations) || varIdx >= startPos.variations.length) {
          console.error('[recordMove] Invalid starting position variation path:', currentMove, { startPos, varIdx });
          return;
        }
        
        const currentVariation = startPos.variations[varIdx];
        const nextMoveExists = moveIdx + 1 < currentVariation.length;
        
        if (!nextMoveExists) {
          // Add to existing starting position variation
          dispatch({ 
            type: 'ADD_VARIATION_MOVE', 
            payload: { 
              path: currentMove,
              notation,
              from: fromSquare,
              to: toSquare,
              ...additionalData
            }
          });
          
          // Navigate to the new move
          const newMoveIdx = moveIdx + 1;
          const newPath = `-1|${varIdx}:${newMoveIdx}`;
          dispatch({ type: 'GOTO_MOVE', payload: newPath });
        } else {
          // Different move - create a nested variation
          const newVariationMove = { 
            notation, 
            from: fromSquare, 
            to: toSquare,
            ...additionalData
          };
          const newNestedVariation = [newVariationMove];
          
          dispatch({ 
            type: 'ADD_VARIATION_AT_PATH', 
            payload: { 
              path: currentMove,
              variation: newNestedVariation
            }
          });
          
          // Navigate to the new nested variation
          const existingVariationsCount = currentVariation[moveIdx]?.variations?.length || 0;
          const newNestedMoveId = `${currentMove}|${existingVariationsCount}:0`;
          dispatch({ type: 'GOTO_MOVE', payload: newNestedMoveId });
        }
        return;
      }
      
      
      // We're already in a variation - need to check if we should add to existing or create nested
      const parts = currentMove.split('|');
      const lastPart = parts[parts.length - 1];
      const [varIdx, moveIdx] = lastPart.split(':').map(Number);
      
      // Get the current variation using the path utilities
      const nodeResult = getNodeAtPathInfinite(moves, currentMove, state);
      
      
      
      if (!nodeResult.success) {
        console.error('[recordMove] Failed to resolve current variation path:', currentMove, nodeResult.error);
        return;
      }
      
      // Check if there's already a next move in this variation
      const currentVariation = nodeResult.nodeArray;
      const nextMoveExists = moveIdx + 1 < currentVariation.length;
      
      
      
      if (!nextMoveExists) {
        // We're at the end of the variation - add to existing variation
        
        
      dispatch({ 
        type: 'ADD_VARIATION_MOVE', 
        payload: { 
          path: currentMove,
          notation,
          from: fromSquare,
          to: toSquare,
            ...additionalData
        }
      });
      
      // Navigate to the new move
      const newMoveIdx = moveIdx + 1;
      const newPath = parts.slice(0, -1).join('|') + `|${varIdx}:${newMoveIdx}`;
      
      
      dispatch({ type: 'GOTO_MOVE', payload: newPath });
      } else {
        // There's already a next move - check if it matches
        const nextMove = currentVariation[moveIdx + 1];
        
        if (nextMove && nextMove.notation === notation) {
          // Move already exists, just navigate to it
          const nextMoveIdx = moveIdx + 1;
          const nextPath = parts.slice(0, -1).join('|') + `|${varIdx}:${nextMoveIdx}`;
          
          
          dispatch({ type: 'GOTO_MOVE', payload: nextPath });
        } else {
          // Different move - create a nested variation at current position
          
          
          const newVariationMove = { 
            notation, 
            from: fromSquare, 
            to: toSquare,
            ...additionalData
          };
          const newNestedVariation = [newVariationMove];
          
          // Use ADD_VARIATION_AT_PATH to add nested variation at current position
          dispatch({ 
            type: 'ADD_VARIATION_AT_PATH', 
            payload: { 
              path: currentMove,
              variation: newNestedVariation
            }
          });
          
          // Get the current node to determine how many variations it already has
          const currentNode = nodeResult.node;
          const existingVariationsCount = currentNode?.variations?.length || 0;
          
          // Navigate to the new nested variation
          const newNestedMoveId = `${currentMove}|${existingVariationsCount}:0`;
          
          
          dispatch({ type: 'GOTO_MOVE', payload: newNestedMoveId });
        }
      }
    } 
    else {
      
      
      // We're at the end of the main line
      dispatch({ 
        type: 'ADD_MOVE', 
        payload: { 
          notation,
          from: fromSquare,
          to: toSquare,
          ...additionalData // Include any additional data like arrows or highlightedSquares
        } 
      });
      
      // Navigate to the newly added move
      const newMoveId = String(moves.length);
      
      
      // Update current move ID without recalculating FEN
      dispatch({ type: 'GOTO_MOVE', payload: newMoveId });
    }
  };
  
  /**
   * Get the detailed move history with from/to squares for highlighting
   * @returns {Array} - Array of move objects with notation, from, and to properties
   */
  const getMoveHistory = () => {
    try {
      // If there are no moves, return an empty array
      if (!moves || moves.length === 0) {
        return [];
      }
      
      // Handle case for variation moves
      if (currentMove.includes('|')) {
        // Parse the variation path
        const parts = currentMove.split('|');
        const mainLineIdx = parseInt(parts[0], 10);
        
        // First check if this is a valid index
        if (mainLineIdx < 0 || mainLineIdx >= moves.length) {
          console.error('[getMoveHistory] Invalid main line index:', mainLineIdx);
          return [];
        }
        
        // If it's a direct child variation (only one level deep)
        if (parts.length === 2) {
          const [varIdx, moveIdx] = parts[1].split(':').map(Number);
          
          // Make sure this variation exists
          if (!moves[mainLineIdx]?.variations?.[varIdx]) {
            console.error('[getMoveHistory] Variation not found:', varIdx);
            return [];
          }
          
          const variation = moves[mainLineIdx].variations[varIdx];
          if (moveIdx < 0 || moveIdx >= variation.length) {
            console.error('[getMoveHistory] Invalid move index in variation:', moveIdx);
            return [];
          }
          
          // Create an array with the main line moves up to mainLineIdx, and then the variation moves
          const mainLineMoves = moves.slice(0, mainLineIdx + 1).map(move => ({
            san: move.notation,
            from: move.from || '',
            to: move.to || ''
          }));
          
          // Add variation moves
          const variationMoves = variation.slice(0, moveIdx + 1).map(move => ({
            san: move.notation,
            from: move.from || '',
            to: move.to || ''
          }));
          
          // Return the full move history
          return [...mainLineMoves, ...variationMoves];
        }
        
        // For deeper nested variations (recursive approach needed)
        else if (parts.length > 2) {
          // This is a complex case that would need recursive tree traversal
          // For now, return just the move at the exact position if we can find it
          
          // Start with the main line move
          let currentNode = moves[mainLineIdx];
          let movesList = [{ 
            san: currentNode.notation,
            from: currentNode.from || '',
            to: currentNode.to || ''
          }];
          
          // Traverse the variation path
          for (let i = 1; i < parts.length; i++) {
            const [varIdx, moveIdx] = parts[i].split(':').map(Number);
            
            // Check if this variation exists
            if (!currentNode?.variations?.[varIdx] || moveIdx >= currentNode.variations[varIdx].length) {
              console.error('[getMoveHistory] Invalid variation path at part:', parts[i]);
              return movesList; // Return what we have so far
            }
            
            // Move to the next node
            currentNode = currentNode.variations[varIdx][moveIdx];
            movesList.push({
              san: currentNode.notation,
              from: currentNode.from || '',
              to: currentNode.to || ''
            });
          }
          
          return movesList;
        }
      }
      
      // For main line moves, simply return the array of move objects
      return moves.map(move => ({
        san: move.notation,
        from: move.from || '',
        to: move.to || ''
      }));
    } catch (error) {
      console.error('[getMoveHistory] Error getting move history:', error);
      return [];
    }
  };
  
  /**
   * Find the node at a specific path in the move tree
   * @param {string} path - The move path
   * @returns {Object} - Info about the node
   */
  const getNodeAtPath = (path) => {
    if (!path || path === '-1') return { node: null, parent: null, index: -1 };
    
    const parts = path.split('|');
    let node = null;
    let parentPath = null;
    let currentMoves = moves;
    
    // Handle main line move
    if (parts.length === 1) {
      const idx = parseInt(parts[0], 10);
      if (idx >= 0 && idx < moves.length) {
        return { 
          node: moves[idx],
          parent: null, 
          index: idx,
          nextSibling: idx + 1 < moves.length ? String(idx + 1) : null,
          prevSibling: idx > 0 ? String(idx - 1) : '-1',
          variations: moves[idx]?.variations || []
        };
      }
      return { node: null, parent: null, index: -1 };
    }
    
    // Handle variation moves
    let currentIndex = parseInt(parts[0], 10);
    parentPath = parts[0];
    node = moves[currentIndex];
    
    for (let i = 1; i < parts.length; i++) {
      const segment = parts[i];
      const [varIdx, moveIdx] = segment.split(':').map(Number);
      
      // For all segments except the last one, continue traversing
      if (i < parts.length - 1) {
        if (!node?.variations?.[varIdx]?.[moveIdx]) {
          return { node: null, parent: parentPath, index: -1 };
        }
        
        node = node.variations[varIdx][moveIdx];
        currentMoves = node.variations?.[varIdx] || [];
        parentPath = parts.slice(0, i + 1).join('|');
        currentIndex = moveIdx;
      } else {
        // Last segment - get the actual node
        const variation = node?.variations?.[varIdx];
        if (!variation || moveIdx >= variation.length) {
          return { node: null, parent: parentPath, index: -1 };
        }
        
        node = variation[moveIdx];
        currentIndex = moveIdx;
        currentMoves = variation;
        
        // Calculate siblings
        const nextSibling = moveIdx + 1 < variation.length 
          ? `${parentPath}|${varIdx}:${moveIdx + 1}` 
          : null;
          
        const prevSibling = moveIdx > 0 
          ? `${parentPath}|${varIdx}:${moveIdx - 1}` 
          : parentPath;
        
        return {
          node,
          parent: parentPath,
          index: currentIndex,
          currentMoves,
          nextSibling,
          prevSibling,
          variations: node?.variations || []
        };
      }
    }
    
    return { node, parent: parentPath, index: currentIndex };
  };

  /**
   * Move to the previous move in the current variation
   */
  const prevMove = () => {
    if (currentMove === '-1') return; // Already at start
    
    if (currentMove.includes('|')) {
      // In a variation
      const parts = currentMove.split('|');
      const lastPart = parts[parts.length - 1];
      
      if (lastPart.includes(':')) {
        const [varIdx, moveIdx] = lastPart.split(':').map(Number);
        
        if (moveIdx > 0) {
          // Move back within variation
          parts[parts.length - 1] = `${varIdx}:${moveIdx - 1}`;
          goToMove(parts.join('|'));
        } else {
          // At start of variation, pop back to parent
          goToMove(parts.slice(0, -1).join('|'));
        }
      }
    } else {
      // In main line
      const idx = parseInt(currentMove, 10);
      if (idx > 0) goToMove((idx - 1).toString());
      else goToMove('-1');
    }
  };

  /**
   * Move to the next move in the current variation
   */
  const nextMove = () => {
    // At start position
    if (currentMove === '-1') {
      if (moves.length > 0) goToMove('0');
      return;
    }
    
    // In a variation
    if (currentMove.includes('|')) {
      const parts = currentMove.split('|');
      const lastPart = parts[parts.length - 1];
      
      if (lastPart.includes(':')) {
        const [varIdx, moveIdx] = lastPart.split(':').map(Number);
        const parentPath = parts.slice(0, -1).join('|');
        
        // Find the current variation
        let currentVariation = null;
        
        // ENHANCED: Handle start position variations
        if (parentPath === '-1') {
          // This is a start position variation
          currentVariation = state?.startPosition?.variations?.[varIdx];
        } else if (parentPath.includes('|')) {
          // Nested variation
          const parentInfo = getNodeAtPath(parentPath);
          currentVariation = parentInfo.node?.variations?.[varIdx];
        } else {
          // Top-level variation
          const parentIdx = parseInt(parentPath, 10);
          currentVariation = moves[parentIdx]?.variations?.[varIdx];
        }
        
        if (currentVariation && moveIdx + 1 < currentVariation.length) {
          // Move forward within variation
          parts[parts.length - 1] = `${varIdx}:${moveIdx + 1}`;
          goToMove(parts.join('|'));
          // console.log(`🎯 [UseGameState] nextMove: Navigated to ${parts.join('|')}`);
        } else {
          // console.log(`🔚 [UseGameState] nextMove: At end of variation`);
        }
      }
    } else {
      // In main line
      const idx = parseInt(currentMove, 10);
      if (idx + 1 < moves.length) {
        goToMove((idx + 1).toString());
      } else if (moves[idx]?.variations?.length > 0) {
        // Enter first variation if available
        goToMove(`${idx}|0:0`);
      }
    }
  };

  /**
   * Jump to the last move in the current context
   * @param {string} context - 'DEFAULT' to filter comment moves, null for all moves
   */
  const jumpToLast = (context = null) => {
    if (!moves || moves.length === 0) return;
    
    // Helper function to check if a move is a comment move
    const isCommentMove = (move) => {
      return move?.creator?.type === 'COMMENT' || 
             move?.creator === 'user' ||
             move?.creator === 'COMMENT' ||
             move?.notation?.includes('(Comment)') ||
             move?.type === 'comment';
    };
    
    // If context is 'DEFAULT', filter out comment moves from main line
    if (context === 'DEFAULT') {
      const filteredMoves = moves.filter(move => !isCommentMove(move));
      if (filteredMoves.length === 0) return;
      
      // Find the original index of the last non-comment move
      let lastMainLineIndex = -1;
      for (let i = moves.length - 1; i >= 0; i--) {
        if (!isCommentMove(moves[i])) {
          lastMainLineIndex = i;
          break;
        }
      }
      
      if (lastMainLineIndex >= 0) {
        
        goToMove(String(lastMainLineIndex));
      }
    } else {
      // Original behavior - go to last move regardless of type
      goToMove(String(moves.length - 1));
    }
  };

// dispatch({ type: 'SET_ROOT_FEN', payload: 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1' });

// dispatch({ type: 'SET_ORIENTATION', payload: 'black' });

// dispatch({ type: 'TOGGLE_STAR_MOVE', payload: '3|0:1' }); // Example move path
const toggleStarredMove = (moveId) => {
  dispatch({ type: 'TOGGLE_STAR_MOVE', payload: moveId });
};

  /**
   * Truncate moves at a specific index - keeps the move at the given index and removes all moves after it
   * @param {string} moveId - ID of the move to truncate at (this move will be kept, all after will be removed)
   * @returns {boolean} - True if the truncation was successful
   */
  const truncateMovesAt = (moveId) => {
    if (!moves || moves.length === 0 || moveId === '-1') {
      return false;
    }
    
    // Handle truncation in a variation
    if (moveId.includes('|')) {
      const parts = moveId.split('|');
      const lastPart = parts[parts.length - 1];
      const [varIdx, moveIdx] = lastPart.split(':').map(Number);
      const parentPath = parts.slice(0, -1).join('|');
      
      // Get the parent node
      const parentNode = getNodeAtPath(parentPath);
      if (!parentNode?.node?.variations?.[varIdx]) {
        return false;
      }
      
      // Create new variations array with truncated variation
      const variation = parentNode.node.variations[varIdx];
      const newVariations = [...parentNode.node.variations];
      
      // Truncate the variation after the current index (keeping the current move)
      newVariations[varIdx] = variation.slice(0, moveIdx + 1);
      
      // Update the variations
      dispatch({
        type: 'UPDATE_VARIATIONS',
        payload: {
          path: parentPath,
          variations: newVariations
        }
      });
      
      // Explicitly stay on the current move
      goToMove(moveId);
      
      return true;
    } else {
      // Handle truncation in the main line
      const currentIdx = parseInt(moveId, 10);
      if (currentIdx < 0 || currentIdx >= moves.length) {
        return false;
      }
      
      // Create a new moves array truncated after the current index (keeping the current move)
      const newMoves = moves.slice(0, currentIdx + 1);
      
      // Update the move list
      dispatch({
        type: 'SET_MOVES',
        payload: newMoves
      });
      
      // Explicitly stay on the current move
      goToMove(moveId);
      
      return true;
    }
  };

  /**
   * Undo the current move and all subsequent moves
   * @param {boolean} forceDelete - Force deletion even if not at the last move
   * @returns {boolean} - True if the undo was successful
   */
  const undoMove = (forceDelete = false) => {
    if (!moves || moves.length === 0 || currentMove === '-1') {
      return false;
    }

    // If we're in a variation, handle that case
    if (currentMove.includes('|')) {
      const parts = currentMove.split('|');
      const lastPart = parts[parts.length - 1];
      const [varIdx, moveIdx] = lastPart.split(':').map(Number);
      const parentPath = parts.slice(0, -1).join('|');
      
      // Get the parent node
      const parentNode = getNodeAtPath(parentPath);
      if (!parentNode?.node?.variations?.[varIdx]) {
        return false;
      }

      const variation = parentNode.node.variations[varIdx];

      // If we're not at the last move in the variation and not forcing deletion, show modal
      if (moveIdx < variation.length - 1 && !forceDelete) {
        window.dispatchEvent(new CustomEvent('open-undo-modal', { 
          detail: { 
            currentMove: state.currentMove 
          } 
        }));
        return false;
      }

      // We're at the last move or forcing deletion, remove it and go back within the same variation
      if (forceDelete) {
        // If forcing deletion, use truncateMovesAt to remove all from current position
        return truncateMovesAt(currentMove);
      } else {
        // Otherwise just remove the last move (original behavior)
        const newVariations = [...parentNode.node.variations];
        newVariations[varIdx] = newVariations[varIdx].slice(0, -1); // Remove last move
        
        // If the variation is now empty, remove it
        if (newVariations[varIdx].length === 0) {
          newVariations.splice(varIdx, 1);
          // If variation is now empty, go back to parent
          goToMove(parentPath);
        } else {
          // Stay in the same variation, just go back one move
          parts[parts.length - 1] = `${varIdx}:${newVariations[varIdx].length - 1}`;
          goToMove(parts.join('|'));
        }

        // Update the parent node's variations
        dispatch({
          type: 'UPDATE_VARIATIONS',
          payload: {
            path: parentPath,
            variations: newVariations
          }
        });
        
        return true;
      }
    } else {
      // We're in the main line
      const currentIdx = parseInt(currentMove, 10);
      if (currentIdx < 0 || currentIdx >= moves.length) {
        return false;
      }

      // If we're not at the last move and not forcing deletion, show modal
      if (currentIdx < moves.length - 1 && !forceDelete) {
        window.dispatchEvent(new CustomEvent('open-undo-modal', { 
          detail: { 
            currentMove: state.currentMove 
          } 
        }));
        return false;
      }

      // We're at the last move or forcing deletion
      if (forceDelete) {
        // If forcing deletion, use truncateMovesAt to remove all from current position
        return truncateMovesAt(currentMove);
      } else {
        // Otherwise just remove the last move (original behavior)
        const newmoves = moves.slice(0, -1); // Remove last move
        
        // Update the move list
        dispatch({
          type: 'SET_MOVES',
          payload: newmoves
        });

        // Go back one move
        goToMove(String(newmoves.length - 1));
        return true;
      }
    }
  };

  // Update FEN whenever moves or currentMove changes
  useEffect(() => {
    
    
    // No need to update FEN immediately after moves are added
    // as the Board component will handle the move first
    if (moves.length === 0 || currentMove === '-1') {
      // Only update FEN for the starting position
      const startFen = state.initialFEN || 'rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1';
      
      
      // Check if the calculated FEN is different from current FEN to prevent unnecessary updates
      if (startFen !== state.currentFEN) {
        
        dispatch({ type: 'SET_FEN', payload: startFen });
      } else {
        
      }
      return;
    }
    
    // Handle starting position variations differently to prevent fallback calculations
    if (currentMove && currentMove.startsWith('-1|')) {
      // For starting position variations, use the GameState context data
      const targetFen = getCurrentBoardPosition(moves, currentMove, state.initialFEN, state);
      
      if (targetFen && targetFen !== state.currentFEN) {
        
        dispatch({ type: 'SET_FEN', payload: targetFen });
      }
      return;
    }
    
    // Try to get stored FEN directly from moves array first (both main line and variations)
    let targetFen = getStoredFENFromMoves(moves, currentMove, state);
    
    if (targetFen) {
      
    } else {
      // Fallback to calculation only if no stored FEN is found
      console.warn('[UseGameState] No stored FEN found for move', currentMove, ', falling back to calculation');
      targetFen = getCurrentBoardPosition(moves, currentMove, state.initialFEN, state);
      
    }
    
    if (targetFen && targetFen !== state.currentFEN) {
      
      dispatch({ type: 'SET_FEN', payload: targetFen });
      
      // Debug log for board FEN changes
      // console.log('🔍 BOARD FEN CHANGED:', {
      //   currentMove,
      //   newFEN: targetFen,
      //   startPosition: state.startPosition,
      //   movesLength: moves.length,
      //   firstMove: moves[0]?.notation,
      //   startPositionVariations: state.startPosition?.variations?.length || 0,
      //   startPositionVariationsDetail: state.startPosition?.variations?.map(variation => 
      //     variation.map(move => ({ notation: move.notation, from: move.from, to: move.to }))
      //   )
      // });
    } else if (targetFen === state.currentFEN) {
      
    } else {
      
    }
  }, [moves, currentMove, state.initialFEN, state.currentFEN, dispatch]);
  
  // Don't update FEN when moves change, only when currentMove changes
  // This prevents conflicts with the Board component's local state

  // Add new utility functions for infinite nesting
  const validateMovePath = (path) => {
    return validatePath(path, state.moves);
  };

  const getPathDepthLevel = (path) => {
    return getPathDepth(path);
  };

  const isCurrentMoveInVariation = () => {
    return isInVariation(state.currentMove);
  };

  const getParentMovePath = (path = state.currentMove) => {
    const segments = parseVariationPath(path);
    if (segments.length <= 1) {
      return '-1';
    }
    const parentSegments = segments.slice(0, -1);
    return buildVariationPath(parentSegments);
  };

  // Enhanced navigation functions
  const navigateToPath = (path) => {
    const validation = validatePath(path, state.moves);
    if (validation.valid) {
      dispatch({ type: 'GOTO_MOVE', payload: path });
      return true;
    } else {
      console.error('[UseGameState] navigateToPath: Invalid path:', validation.error);
      return false;
    }
  };

  const addVariationAtCurrentPosition = (variation) => {
    // Allow variations at any position, including starting position (-1)

    dispatch({
      type: 'ADD_VARIATION_AT_PATH',
      payload: {
        path: state.currentMove,
        variation
      }
    });
    return true;
  };

  const deleteVariationAtPath = (path, variationIndex) => {
    dispatch({
      type: 'DELETE_VARIATION_AT_PATH',
      payload: {
        path,
        variationIndex
      }
    });
  };

  const updateMovePropertiesAtPath = (path, properties) => {
    dispatch({
      type: 'UPDATE_MOVE_PROPERTIES_AT_PATH',
      payload: {
        path,
        properties
      }
    });
  };

  // console.log('🔥 [UseGameState] Hook returning state:', {
  //   currentFEN: state.currentFEN,
  //   initialFEN: state.initialFEN,
  //   currentMove: state.currentMove,
  //   movesLength: moves?.length || 0,
  //   source: 'useGameLogic-return'
  // });

  return {
    // Existing state and functions
    moves,
    currentMove,
    currentFEN: state.currentFEN,
    initialFEN: state.initialFEN,
    orientation: state.orientation,
    starredMoves,
    goToMove,
    setMoves,
    setInitialPosition,
    recordMove,
    getNextMove,
    prevMove,
    nextMove,
    jumpToLast,
    undoMove,
    toggleStarredMove,
    getMoveHistory,
    dispatch,
    state,
    
    // NEW: Infinite nesting utilities
    validateMovePath,
    getPathDepthLevel,
    isCurrentMoveInVariation,
    getParentMovePath,
    navigateToPath,
    addVariationAtCurrentPosition,
    deleteVariationAtPath,
    updateMovePropertiesAtPath,
    
    // NEW: Direct access to utility functions
    parseVariationPath,
    buildVariationPath,
    getNodeAtPathInfinite,
    getMoveSequenceToPath,
    getNextMovePath,
    getPreviousMovePath,
    isInVariation,
    getPathDepth
  };
};

export default useGameLogic;
export { getCurrentBoardPosition, getStoredFENFromMoves };